import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const faqData = [
  {
    q: "How do I appeal an Instagram account suspension?",
    a: "If Instagram disabled your account, you have 30 days to file an appeal through the official channel at help.instagram.com/contact/606967319425038. Steps: (1) Save a screenshot of every notice Instagram sent you. (2) Confirm which Community Guideline category was cited; this determines the appeal path. (3) Gather identity verification (government ID matching the account's registered name) and business documentation if it's a business account. (4) Submit a concise, policy-specific appeal through the official form — do not file multiple duplicate appeals, which Instagram may treat as spam. (5) Expect a response in 24-72 hours; if denied, a second appeal via the Oversight Board option may be available. If you'd like us to prepare the full documentation and submit on your behalf, our Appeal Package ($399) covers that end-to-end with your written authorization."
  },
  {
    q: "Do you provide outcome guarantees?",
    a: "No. Social Dispute Co prepares documentation and submits appeals through official platform channels, but does not control platform decisions. Outcomes depend on platform review and the facts of each case. See our refund policy for details."
  },
  {
    q: "Who actually submits my appeal — you or me?",
    a: "This depends on the tier you pick. The $99 Case Review is a documentation-and-guidance package: we prepare everything, you submit it yourself. The $399 Appeal Package and $499 Priority Support include submission on your behalf, with your written authorization, through the platform's official public appeal channel. We never claim insider access or alternative submission paths — every appeal goes through the same channel anyone else would use."
  },
  {
    q: "What is your refund policy?",
    a: "If the platform issues a final denial after our documentation has been submitted and our full process is complete, a partial refund of $200 may be available on the Appeal Package and Priority Support tiers. See our refund policy page for full details and eligibility."
  },
  {
    q: "Are you a law firm?",
    a: "No. Social Dispute Co is not a law firm and does not provide legal advice. We provide documentation preparation and, for Appeal Package and Priority Support tiers, submission through official platform channels."
  },
  {
    q: "Are you affiliated with Instagram, Facebook, TikTok, YouTube, or X?",
    a: "No. We are an independent service operated by 8eight8 LLC, based in Honolulu, Hawaii. We have no affiliation with Meta, ByteDance, Google, X Corp, or any other platform. We do not have insider access, backchannel submission paths, or employee contacts at any platform."
  },
  {
    q: "Do you contact platforms on my behalf?",
    a: "For the Appeal Package ($399) and Priority Support ($499), yes — we submit the appeal through the platform's official public form using your written authorization. We do not call, email, or message platform employees directly, because no such legitimate channel exists. For the Case Review ($99) tier, we prepare the documentation and guidance; you submit it yourself."
  },
  {
    q: "Why do all my accounts get disabled at once?",
    a: "Platforms may apply cross-account enforcement triggered by automated systems or association signals (shared email, phone, IP, device fingerprint, or payment method), which can cascade across connected assets."
  },
  {
    q: "What if I actually violated the rules?",
    a: "We help you acknowledge the situation properly, document any corrective actions, and provide context for your appeal submission. Honest acknowledgment combined with a demonstrated corrective action often performs better in appeal review than denial. Outcomes depend on platform review."
  },
  {
    q: "How is this different from filing my own appeal?",
    a: "Platform reviewers work from specific policy sub-category codes, timelines, and evidence formats. We map your notice language to the exact sub-policy, reconstruct the timeline of flagged activity, organize evidence into a submittable packet, and (on the Appeal Package and Priority Support tiers) submit through the official channel on your behalf. On the Case Review tier, we hand you the organized package and you file it yourself."
  },
  {
    q: "What if my account was compromised?",
    a: "We compile relevant evidence — login records, IP addresses, identity verification — and organize documentation to support your appeal submission through official channels. Each platform has a specific hacked-account recovery flow (Instagram's selfie-video prompt, Facebook's Trusted Contacts path, TikTok's phone/email reset); we align documentation to the correct flow."
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[#3f4816]/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left group"
      >
        <span className="text-lg font-medium text-[#dfddd6] group-hover:text-[#d9fb06] transition-colors pr-4">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-[#d9fb06] flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      {isOpen && (
        <div className="pb-6">
          <p className="text-[#888680] leading-relaxed">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  // FAQPage JSON-LD so Google and AI engines can pull the Q&A as rich snippets.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-12">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-[#dfddd6] leading-relaxed">
              Get answers to common questions about our documentation services. Don't see your question? Contact us directly.
            </p>
          </div>
        </section>

        {/* FAQ List */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-16">
          <div className="max-w-3xl">
            {faqData.map((faq) => (
              <FAQItem key={faq.q} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="bg-[#302f2c] rounded-2xl p-8 md:p-10 border border-[#3f4816]/50">
              <h2 className="text-2xl font-bold text-[#d9fb06] mb-4">
                Still have questions?
              </h2>
              <p className="text-[#dfddd6] mb-6">
                Our team is ready to help you understand your options.
              </p>
              <a 
                href="mailto:intake@socialdisputeco.com"
                className="inline-flex items-center gap-3 text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors font-medium"
              >
                <Mail className="w-5 h-5" />
                intake@socialdisputeco.com
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
